import { RouteResponse } from "@0xsquid/sdk/dist/types";
import { FeeType } from "@0xsquid/squid-types";
import { useMemo } from "react";
import { findToken, formatUsdAmount } from "../../services";
import {
  calculateEstimateResults,
  calculateTotalWithRefundEstimate,
} from "../../services/internal/estimateService";
import { useConfigStore } from "../store/useSquidStore";
import { useSwap } from "../swap/useSwap";
import { useMultiChainBalance } from "../tokens/useMultiChainBalance";
import { useSingleTokenPrice } from "../tokens/useSingleTokenPrice";
import { useSourceChainGasToken } from "../tokens/useSourceChainGasToken";
import { useSquidTokens } from "../tokens/useSquidTokens";

type FeeFormatted = {
  name: string;
  amountUsdFormatted: string;
  imageUrl: string;
};

const DEFAULT_PROVIDER_IMAGE_URL =
  "https://raw.githubusercontent.com/0xsquid/assets/main/images/webp128/providers/squid.webp";
const AXELAR_PROVIDER_IMAGE_URL =
  "https://raw.githubusercontent.com/0xsquid/assets/main/images/webp128/providers/axelar.webp";

export const useEstimate = (squidRoute?: RouteResponse["route"]) => {
  const collectFees = useConfigStore((state) => state.config.collectFees);

  const { tokens } = useSquidTokens();
  const { fromChain, toChain, fromPrice } = useSwap();

  const fromToken = useMemo(
    () =>
      findToken(
        tokens,
        squidRoute?.params.fromChain,
        squidRoute?.params.fromToken
      ),
    [tokens, squidRoute?.params.fromChain, squidRoute?.params.fromToken]
  );

  const { chainFeeParams, gasToken } = useSourceChainGasToken({
    fromChain,
    fromToken,
  });

  const estimateResults = useMemo(
    () =>
      calculateEstimateResults({
        squidRoute,
        tokens,
        fromChain,
        toChain,
        collectFees: !!collectFees && collectFees.fee > 0,
        chainFeeParams,
        gasToken,
      }),
    [
      squidRoute,
      tokens,
      fromChain,
      toChain,
      collectFees,
      chainFeeParams,
      gasToken,
    ]
  );

  const fromBalanceFormatted =
    useMultiChainBalance({
      chain: fromChain,
      token: estimateResults.fromToken,
      userAddress: squidRoute?.params.fromAddress ?? "",
      enabled: !!squidRoute,
    }).balance ?? "0";

  const { getUSDValue } = useSingleTokenPrice(
    estimateResults.firstFeeCost?.token
  );

  const totalWithRefundEstimate = useMemo(
    () =>
      calculateTotalWithRefundEstimate(
        estimateResults.allFeeCosts,
        estimateResults.expectedGasRefundCost,
        getUSDValue
      ),
    [
      estimateResults.allFeeCosts,
      estimateResults.expectedGasRefundCost,
      getUSDValue,
    ]
  );

  const { feeCostsFormatted, totalFeeCostsUsd } = useMemo<{
    feeCostsFormatted: FeeFormatted[];
    totalFeeCostsUsd: string;
  }>(() => {
    const feeCosts = squidRoute?.estimate.feeCosts ?? [];
    const feeCostsRenamed: {
      name: string;
      amountUsd: number;
      imageUrl: string;
    }[] = [];

    const feesToRename = [
      {
        newName: "CORAL fee",
        match: [FeeType.EXECUTION_FEE, FeeType.SETTLEMENT_FEE],
        imageUrl: DEFAULT_PROVIDER_IMAGE_URL,
      },
      {
        newName: "Axelar fee",
        match: [FeeType.GAS_RECEIVER_FEE, FeeType.BOOST_FEE],
        imageUrl: AXELAR_PROVIDER_IMAGE_URL,
      },
    ];

    for (const fee of feeCosts) {
      if (Number(fee.amountUsd) === 0) continue;

      const feeToRename = feesToRename.find(({ match }) =>
        match.includes(fee.name)
      );

      if (feeToRename) {
        const previousRenamedFeeIndex = feeCostsRenamed.findIndex(
          (f) => f.name === feeToRename.newName
        );

        if (feeCostsRenamed[previousRenamedFeeIndex] == null) {
          feeCostsRenamed.push({
            name: feeToRename.newName,
            amountUsd: Number(fee.amountUsd),
            imageUrl: feeToRename.imageUrl,
          });
        } else {
          feeCostsRenamed[previousRenamedFeeIndex].amountUsd += Number(
            fee.amountUsd
          );
        }
      } else {
        feeCostsRenamed.push({
          name: fee.name,
          amountUsd: Number(fee.amountUsd),
          imageUrl: fee.logoURI || DEFAULT_PROVIDER_IMAGE_URL,
        });
      }
    }

    const feeCostsFormatted: FeeFormatted[] = feeCostsRenamed.map((fee) => ({
      name: fee.name,
      imageUrl: fee.imageUrl,
      amountUsdFormatted: formatUsdAmount(fee.amountUsd, {
        decimalPrecision: true,
      }),
    }));

    const totalFeeCostsUsd = feeCosts.reduce((totalFees, currentFee) => {
      return Number(totalFees) + Number(currentFee.amountUsd);
    }, 0);

    const totalFeeCostsUsdFormatted = formatUsdAmount(totalFeeCostsUsd, {
      decimalPrecision: true,
    });

    return {
      feeCostsFormatted,
      totalFeeCostsUsd: totalFeeCostsUsdFormatted,
    };
  }, [squidRoute?.estimate.feeCosts]);

  const slippageFormatted =
    Number(squidRoute?.estimate?.aggregateSlippage ?? 0).toFixed(2) + "%";

  const fromBalanceEnoughToSwap = useMemo(() => {
    const fromBalanceNum = Number(fromBalanceFormatted ?? 0);
    const fromPriceNum = Number(fromPrice ?? 0);
    return fromBalanceNum >= fromPriceNum;
  }, [fromBalanceFormatted, fromPrice]);

  return {
    ...estimateResults,
    fromBalanceFormatted,
    slippageFormatted,
    totalWithRefundEstimate,
    fromBalanceEnoughToSwap,
    feeCostsFormatted,
    totalFeeCostsUsd,
    gasToken,
  };
};
